/**
 * Pure conversion: Fabric primitive (Rect / Circle / Triangle) → editor
 * `VectorPath`. Extracted from `useFabricCanvas.getShapeVectorPath` so
 * the geometry + transform math is testable without spinning up the
 * canvas hook.
 *
 * `getShapeVectorPath` is the hook's thin wrapper around this helper:
 * it queries Fabric for the object bound to a layerId, snapshots
 * stroke / fill from the object's props, and delegates here for the
 * per-type local-space → scene-space conversion.
 */

import * as fabric from 'fabric';
import type { VectorAnchor, VectorPath, VectorStroke, ShapeFillValue } from '../types';

/** Bezier control-point ratio for a quarter-circle. Max approximation
 *  error against a true circle is ~0.0006 of the radius — well below
 *  PSD's rendering resolution. */
const CIRCLE_KAPPA = 0.5522847498;

/** Convert a Fabric Rect / Circle / Triangle to a scene-space VectorPath
 *  using the object's own `calcTransformMatrix()`. Returns null for
 *  any other Fabric subclass (Line / Path / Image / IText / Group) so
 *  the caller can route to its raster fallback. */
export function fabricShapeToVectorPath(
  obj: fabric.FabricObject,
  fillResolver: (fill: fabric.FabricObject['fill']) => string | ShapeFillValue,
): VectorPath | null {
  const strokeColor = typeof obj.stroke === 'string' ? obj.stroke : '#000000';
  const strokeWidth = obj.strokeWidth ?? 0;
  const stroke: VectorStroke | undefined =
    strokeWidth > 0 && strokeColor && strokeColor !== 'transparent' && strokeColor !== ''
      ? { color: strokeColor, width: strokeWidth }
      : undefined;
  const fillResolved = fillResolver(obj.fill);

  obj.setCoords();
  const m = obj.calcTransformMatrix();
  // Apply the affine matrix to a local-space point. Matrix layout
  // matches Fabric / Canvas2D: [a, b, c, d, e, f] where the implicit
  // 3rd column is [0, 0, 1].
  const tx = (x: number, y: number) => ({
    x: m[0] * x + m[2] * y + m[4],
    y: m[1] * x + m[3] * y + m[5],
  });
  // Same matrix without the translation — used for relative Bezier
  // control handles (handles are relative to their anchor, so they
  // only get rotation / scale).
  const txHandle = (x: number, y: number) => ({
    x: m[0] * x + m[2] * y,
    y: m[1] * x + m[3] * y,
  });

  if (obj instanceof fabric.Rect) {
    const w = obj.width ?? 0;
    const h = obj.height ?? 0;
    const localCorners = [
      { x: -w / 2, y: -h / 2 },
      { x: w / 2, y: -h / 2 },
      { x: w / 2, y: h / 2 },
      { x: -w / 2, y: h / 2 },
    ];
    const anchors: VectorAnchor[] = localCorners.map((p) => {
      const t = tx(p.x, p.y);
      return { x: t.x, y: t.y, type: 'corner' };
    });
    return { subPaths: [{ closed: true, anchors }], stroke, fill: fillResolved };
  }

  if (obj instanceof fabric.Circle) {
    const r = (obj as fabric.Circle).radius ?? 0;
    const cp = r * CIRCLE_KAPPA;
    const localAnchors = [
      { x: 0, y: -r, inX: -cp, inY: 0, outX: cp, outY: 0 },
      { x: r, y: 0, inX: 0, inY: -cp, outX: 0, outY: cp },
      { x: 0, y: r, inX: cp, inY: 0, outX: -cp, outY: 0 },
      { x: -r, y: 0, inX: 0, inY: cp, outX: 0, outY: -cp },
    ];
    const anchors: VectorAnchor[] = localAnchors.map((p) => {
      const t = tx(p.x, p.y);
      const inH = txHandle(p.inX, p.inY);
      const outH = txHandle(p.outX, p.outY);
      return {
        x: t.x,
        y: t.y,
        inX: inH.x,
        inY: inH.y,
        outX: outH.x,
        outY: outH.y,
        type: 'smooth',
      };
    });
    return { subPaths: [{ closed: true, anchors }], stroke, fill: fillResolved };
  }

  if (obj instanceof fabric.Triangle) {
    const w = obj.width ?? 0;
    const h = obj.height ?? 0;
    // Fabric.Triangle is an isoceles triangle inscribed in
    // `width × height` with the apex at the top (y = -h/2) and the
    // base centered on the bottom.
    const localCorners = [
      { x: 0, y: -h / 2 },
      { x: w / 2, y: h / 2 },
      { x: -w / 2, y: h / 2 },
    ];
    const anchors: VectorAnchor[] = localCorners.map((p) => {
      const t = tx(p.x, p.y);
      return { x: t.x, y: t.y, type: 'corner' };
    });
    return { subPaths: [{ closed: true, anchors }], stroke, fill: fillResolved };
  }

  // fabric.Line and any future shape — fall back to raster.
  return null;
}
